const WishlistManager = (() => {
  const KEY = 'alto_wishlist';

  function getItems() {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch { return []; }
  }

  function save(items) {
    localStorage.setItem(KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items } }));
  }

  return {
    getItems,
    addItem(productId, handle, variantId, productColor) {
      const items = getItems();
      if (items.find(i => i.productId === productId)) return;
      items.push({ productId, handle, variantId, productColor });
      save(items);
    },
    removeItem(productId) {
      save(getItems().filter(i => i.productId !== productId));
    },
    toggle(productId, handle, variantId, productColor) {
      if (this.isInWishlist(productId)) { this.removeItem(productId); return false; }
      this.addItem(productId, handle, variantId, productColor);
      return true;
    },
    isInWishlist(productId) { return getItems().some(i => i.productId === productId); },
    count() { return getItems().length; }
  };
})();

// ─── WishlistToggle ──────────────────────────────────────────────────────────
// Wraps the heart button on the PDP. Reads the currently selected variant
// from the product form at click time so it always saves the right variant.

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('button');
    const productId = parseInt(this.dataset.productId);
    const handle = this.dataset.handle;
    const productColor = this.dataset.productColor;
    this._update(btn, productId);

    btn.addEventListener('click', () => {
      const formInput = document.querySelector('.product-form [name="id"]');
      const variantId = formInput ? parseInt(formInput.value) : null;
      WishlistManager.toggle(productId, handle, variantId, productColor);
    });

    document.addEventListener('wishlist:changed', () => this._update(btn, productId));
  }

  _update(btn, productId) {
    const active = WishlistManager.isInWishlist(productId);
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    btn.setAttribute('aria-pressed', String(active));
    btn.dataset.active = String(active);
  }
}

customElements.define('wishlist-toggle', WishlistToggle);

// ─── WishlistPage ────────────────────────────────────────────────────────────
// Clones HTML from <template> elements defined in alto-wishlist.liquid and
// fills them with product data fetched from /products/{handle}.js.

class WishlistPage extends HTMLElement {
  _cache = {};
  _skipNextRender = false;

  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this._render();
    document.addEventListener('wishlist:changed', () => this._render());
  }

  async _render() {
    if (this._skipNextRender) { this._skipNextRender = false; return; }

    const items = WishlistManager.getItems();

    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = items.length ? `(${items.length})` : '';
    });

    if (!items.length) { this._setHeaderVisible(false); this._showTemplate('wishlist-empty-tpl'); return; }

    this._setHeaderVisible(true);

    const toFetch = items.filter(({ handle }) => !this._cache[handle]);
    if (toFetch.length) {
      this._showTemplate('wishlist-loading-tpl');
      const fetched = await Promise.all(
        toFetch.map(({ handle }) =>
          fetch(`/products/${handle}.js`).then(r => r.ok ? r.json() : null).catch(() => null)
        )
      );
      toFetch.forEach(({ handle }, i) => {
        if (fetched[i]) this._cache[handle] = fetched[i];
      });
    }

    // Prune stale entries (deleted products)
    items.forEach(({ productId, handle }) => {
      if (!this._cache[handle]) WishlistManager.removeItem(productId);
    });

    const valid = items.filter(({ handle }) => this._cache[handle]);
    if (!valid.length) { this._setHeaderVisible(false); this._showTemplate('wishlist-empty-tpl'); return; }

    const ul = document.createElement('ul');
    ul.setAttribute('role', 'list');
    ul.className = 'tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:md:grid-cols-3 tw:xl:grid-cols-4 tw:gap-6 tw:list-none tw:m-0 tw:p-0';

    valid.forEach(({ variantId, handle, productColor }) => {
      const li = document.createElement('li');
      li.appendChild(this._buildCard(this._cache[handle], variantId, productColor));
      ul.appendChild(li);
    });

    this.replaceChildren(ul);
    if (window.altoInitDropdowns) window.altoInitDropdowns();
    this._bindEvents();
  }

  _setHeaderVisible(visible) {
    document.querySelectorAll('[data-wishlist-header]').forEach(el => {
      el.hidden = !visible;
    });
  }

  _showTemplate(id) {
    const template = document.getElementById(id);
    if (template) this.replaceChildren(template.content.cloneNode(true));
  }

  _buildCard(product, savedVariantId, productColor) {
    const tpl = document.getElementById('wishlist-card-tpl');
    const card = tpl.content.cloneNode(true).firstElementChild;

    const saved = product.variants.find(v => v.id === savedVariantId)
      || product.variants.find(v => v.available)
      || product.variants[0];

    const variantId = saved?.id;
    const price = saved ? this._money(saved.price) : '';
    const subtitle = productColor || '';
    const image = product.images[0] || '';

    card.dataset.productId = product.id;

    card.querySelector('[data-card-link]').href = `/products/${product.handle}`;

    const img = card.querySelector('[data-card-image]');
    if (image) { img.src = image.replace(/(\.[^.?]+)(\?|$)/, '_600x$1$2'); img.alt = product.title; }
    else { img.remove(); }

    card.querySelector('[data-card-title]').textContent = product.title;

    const subtitleEl = card.querySelector('[data-card-subtitle]');
    if (subtitle) { subtitleEl.textContent = subtitle; subtitleEl.hidden = false; }

    card.querySelector('[data-card-price]').textContent = price;

    const dropdownInput = card.querySelector('input[name="variant"]');
    const panel = card.querySelector('[role="listbox"]');
    const triggerLabel = card.querySelector('.alto-dropdown__label');

    dropdownInput.dataset.productId = product.id;

    product.variants.forEach(v => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.dataset.value = v.id;
      li.dataset.label = v.title;
      li.setAttribute('aria-selected', String(v.id === variantId));
      if (!v.available) li.className = 'tw:opacity-50 tw:pointer-events-none';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'alto-dropdown__option';
      btn.textContent = v.title + (!v.available ? ' (Sold out)' : '');
      li.appendChild(btn);
      panel.appendChild(li);
    });

    const selectedVariant = product.variants.find(v => v.id === variantId);
    if (selectedVariant) {
      dropdownInput.value = variantId;
      if (triggerLabel) triggerLabel.textContent = selectedVariant.title;
    }

    const addBtn = card.querySelector('[data-add-to-cart]');
    addBtn.dataset.productId = product.id;
    addBtn.dataset.variantId = variantId;

    card.querySelector('[data-wishlist-remove]').dataset.productId = product.id;

    return card;
  }

  _bindEvents() {
    this.querySelectorAll('input[name="variant"]').forEach(input => {
      input.addEventListener('change', () => {
        const btn = this.querySelector(`[data-add-to-cart][data-product-id="${input.dataset.productId}"]`);
        if (btn) btn.dataset.variantId = input.value;
      });
    });

    this.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', () => this._addToCart(btn));
    });

    this.querySelectorAll('[data-wishlist-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = parseInt(btn.dataset.productId);
        const li = btn.closest('li');

        this._skipNextRender = true;
        if (li) li.remove();

        WishlistManager.removeItem(productId);

        if (!WishlistManager.count()) this._showTemplate('wishlist-empty-tpl');

        document.querySelectorAll('[data-wishlist-count]').forEach(el => {
          const n = WishlistManager.count();
          el.textContent = n ? `(${n})` : '';
        });
      });
    });
  }

  async _addToCart(btn) {
    const variantId = parseInt(btn.dataset.variantId);
    if (!variantId) return;

    const labelEl = btn.querySelector('[data-cta-label]') || btn;
    const label = labelEl.textContent;
    btn.disabled = true;
    btn.classList.add('loading');
    btn.setAttribute('aria-busy', 'true');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname })
      });

      if (!res.ok) throw new Error();
      const state = await res.json();

      if (cartDrawer && state.sections) cartDrawer.renderContents(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist', cartData: state });

      btn.classList.remove('loading');
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    } catch {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-busy');
      labelEl.textContent = label;
      btn.disabled = false;
    }
  }

  _money(cents) {
    const fmt = this.moneyFormat;
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
  }
}

customElements.define('wishlist-page', WishlistPage);
